"use client"

import { toast } from "sonner"
import { Database, AlertCircle, Info, AlertTriangle, Calendar } from "lucide-react"

export interface ToastOptions {
  title: string
  description?: string
  variant?: 'success' | 'error' | 'info' | 'warning'
  action?: {
    label: string
    onClick: () => void
  }
  duration?: number
}

export function showToast({ title, description, variant = 'info', action, duration }: ToastOptions) {
  const getIcon = () => {
    switch (variant) {
      case 'success':
        return <Database className="h-4 w-4" />
      case 'error':
        return <AlertCircle className="h-4 w-4" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />
      case 'info':
      default:
        return <Info className="h-4 w-4" />
    }
  }

  const toastMethod = variant === 'error' ? toast.error : variant === 'success' ? toast.success : variant === 'warning' ? toast.warning : toast.info

  return toastMethod(title, {
    description,
    action,
    icon: getIcon(),
    duration
  })
}

export function dismissToast(toastId: string | number) {
  toast.dismiss(toastId)
}

export function updateToast(toastId: string | number, options: ToastOptions) {
  const getIcon = () => {
    switch (options.variant) {
      case 'success':
        return <Database className="h-4 w-4" />
      case 'error':
        return <AlertCircle className="h-4 w-4" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />
      case 'info':
      default:
        return <Info className="h-4 w-4" />
    }
  }

  const toastMethod = options.variant === 'error' ? toast.error : options.variant === 'success' ? toast.success : options.variant === 'warning' ? toast.warning : toast.info

  return toastMethod(options.title, {
    id: toastId,
    description: options.description,
    action: options.action,
    icon: getIcon(),
    duration: options.duration
  })
}

// Specific toast molecules for common use cases
export const Sonner = {
  saved: (itemName: string, context?: string) =>
    showToast({
      title: "Saved",
      description: context 
        ? `${itemName} ${context} has been saved`
        : `${itemName} has been saved`,
      variant: 'success'
    }),

  scheduleOverrideSaved: (date: string) =>
    showToast({
      title: "Override Saved",
      description: `Availability override for ${date} has been saved`,
      variant: 'success'
    }),

  error: (medicationName: string, error: string) =>
    showToast({
      title: "Update Failed",
      description: `Failed to update ${medicationName}: ${error}`,
      variant: 'error'
    }),

  saving: (medicationName: string) =>
    showToast({
      title: "Saving...",
      description: `Updating ${medicationName} information`,
      variant: 'info'
    }),

  copied: (refNumber: string) =>
    showToast({
      title: "Copied",
      description: `${refNumber} copied to clipboard`,
      variant: 'success'
    }),

  appointmentRescheduled: (appointmentDetails: string) =>
    toast("Appointment Rescheduled", {
      description: `Your appointment has been rescheduled to ${appointmentDetails}`,
      icon: <Calendar className="h-4 w-4" />
    }),

  appointmentBooked: (providerName: string, timezoneInfo?: {
    patientTime?: string
    patientTimezone?: string
    providerTime?: string
    providerTimezone?: string
  }) => {
    let description = `Your appointment with ${providerName} has been successfully scheduled`
    
    if (timezoneInfo?.patientTime && timezoneInfo?.patientTimezone) {
      const shortTimezone = timezoneInfo.patientTimezone.split('/').pop()?.replace('_', ' ') || 'Local'
      description += ` for ${timezoneInfo.patientTime} (${shortTimezone})`
    }
    
    return toast("Appointment Booked", {
      description,
      icon: <Calendar className="h-4 w-4" />
    })
  },

  appointmentBookedWithTimezone: (
    providerName: string,
    appointmentDate: string,
    patientTime: string,
    patientTimezone: string,
    providerTime?: string,
    providerTimezone?: string
  ) => {
    const patientTimezoneShort = patientTimezone.split('/').pop()?.replace('_', ' ') || 'Local'
    let description = `Scheduled for ${appointmentDate} at ${patientTime} (${patientTimezoneShort})`
    
    if (providerTime && providerTimezone && providerTimezone !== patientTimezone) {
      const providerTimezoneShort = providerTimezone.split('/').pop()?.replace('_', ' ') || 'Provider'
      description += ` / ${providerTime} (${providerTimezoneShort})`
    }
    
    return toast("Appointment Confirmed!", {
      description,
      icon: <Calendar className="h-4 w-4" />
    })
  }
}

// Keep MedicationToast for backward compatibility
export const MedicationToast = Sonner